import type { Lang } from "./lang";

/**
 * Shared UI chrome labels used across pages: button text, breadcrumb-style
 * links, calendar controls, and the honest "coming soon" placeholder line.
 * Page _content_ (the verbatim paragraphs from the old site) is carried
 * per-language inside each content module instead of here — this file is
 * only for the handful of small labels every page's chrome can reuse.
 */
export type UiKey =
  | "knowMore"
  | "moreAbout"
  | "readMore"
  | "download"
  | "joinOnZoom"
  | "watchRecording"
  | "backToGallery"
  | "viewAlbum"
  | "today"
  | "previousMonth"
  | "nextMonth"
  | "calendarScrollHint"
  | "sectionComingSoonLede"
  | "whatsappGroup"
  | "iWantToRecite"
  | "getItOnGooglePlay"
  | "downloadOnAppStore"
  | "close";

/**
 * `hi` translates every label the old site plausibly carried in Hindi. For
 * the rest, `hi` falls back to the English value (marked at each site
 * below) — mostly labels that only appear on pages the B2 pre-flight scan
 * found were English-only under `/hi` on the old site (weekly sessions,
 * gallery, and events calendar), plus "More About", whose old-site Hindi
 * wording we could not confirm.
 */
export const DICT = {
  en: {
    knowMore: "Know More",
    moreAbout: "More About",
    readMore: "Read More",
    download: "Download",
    joinOnZoom: "Join on Zoom",
    watchRecording: "Watch Recording",
    backToGallery: "Back to Gallery",
    viewAlbum: "View Album",
    today: "Today",
    previousMonth: "Previous Month",
    nextMonth: "Next Month",
    calendarScrollHint: "Swipe the calendar to see the whole week.",
    sectionComingSoonLede:
      "We're preparing this section. In the meantime, explore our daily practice and talks.",
    whatsappGroup: "WhatsApp Group",
    iWantToRecite: "I Want to Recite",
    getItOnGooglePlay: "Get it on Google Play",
    downloadOnAppStore: "Download on the App Store",
    close: "Close",
  },
  hi: {
    knowMore: "अधिक जानें",
    // No confirmed old-site Hindi wording for this label; falls back to English.
    moreAbout: "More About",
    readMore: "और पढ़ें",
    download: "डाउनलोड करें",
    // The weekly-session pages had no Hindi rendering on the old site; falls back to English.
    joinOnZoom: "Join on Zoom",
    // The weekly-session pages had no Hindi rendering on the old site; falls back to English.
    watchRecording: "Watch Recording",
    // The gallery had no Hindi rendering on the old site; falls back to English.
    backToGallery: "Back to Gallery",
    // The gallery had no Hindi rendering on the old site; falls back to English.
    viewAlbum: "View Album",
    // The events calendar had no Hindi rendering on the old site; falls back to English.
    today: "Today",
    // The events calendar had no Hindi rendering on the old site; falls back to English.
    previousMonth: "Previous Month",
    // The events calendar had no Hindi rendering on the old site; falls back to English.
    nextMonth: "Next Month",
    // Affordance added in this rebuild; the calendar surface stays English.
    calendarScrollHint: "Swipe the calendar to see the whole week.",
    sectionComingSoonLede:
      "हम यह अनुभाग तैयार कर रहे हैं। तब तक हमारे दैनिक अभ्यास और प्रवचन देखें।",
    whatsappGroup: "व्हाट्सएप ग्रुप",
    iWantToRecite: "मुझे पाठ करना है",
    getItOnGooglePlay: "Google Play पर पाएं",
    downloadOnAppStore: "App Store से डाउनलोड करें",
    close: "बंद करें",
  },
} satisfies Record<Lang, Record<UiKey, string>>;

/** Look up a shared UI chrome label for the given language. */
export function t(lang: Lang, key: UiKey): string {
  return DICT[lang][key];
}
